import BrowserEnvironmentResolver from '@/helpers/environment.helper'
import { getAppMetadata } from '@/plan/metadata/metadata'
import {
  getIdsForBrands,
  getIdsForSubCategories,
  getIdsForTags,
  getIdsForTopCategories,
} from '@/services/pois-tileset.service'
import { POI_LAYER } from '../constant'
import layerService from '@/layers/layerService'

/**
 * POIs Filter Cache System
 *
 * This module provides a high-performance cache for POI filter values
 * that gets updated when the Vuex store changes, but allows direct
 * access during filtering without going through the store.
 */
export interface POIsFilterCacheProximityElements {
  subCategories: Set<number>
  topCategories: Set<number>
  brands: Set<number>
  locations: Set<string>
  tags: Set<number>
  countries: Set<number>
}

interface POIsFilterCache {
  proximity: POIsFilterCacheProximityElements
  bufferDistance: [number, number]
}

interface Filter {
  proximity: {
    min_distance: number
    max_distance: number
  }
  countries: number[]
}
interface PlanProximity {
  proximity: {
    categories: string[]
    brands: string[]
    locations: string[]
    tags: string[]
  }
  filters: Filter[]
}

const DEFAULT_CACHE: POIsFilterCache = {
  proximity: {
    subCategories: new Set(),
    topCategories: new Set(),
    brands: new Set(),
    locations: new Set(),
    tags: new Set(),
    countries: new Set(),
  },
  bufferDistance: [-1, -1],
}

class POIsFilterCacheManager {
  private cache: POIsFilterCache = DEFAULT_CACHE

  /**
   * Bumped on every cache change, so layers whose filter accessors read this
   * cache can key their `updateTriggers` off it instead of a timestamp.
   */
  private generation = 0

  getGeneration(): number {
    return this.generation
  }

  /**
   * Updates the cache with new filter values
   * Called when the plan changes in Vuex store
   */
  async updateCache(plan: PlanProximity): Promise<void> {
    const metadata = getAppMetadata()
    const environmentResolver = BrowserEnvironmentResolver.getInstance()

    const [subCategories, topCategories, brands, tags] = await Promise.all([
      getIdsForSubCategories(
        metadata,
        environmentResolver,
        plan.proximity?.categories || [],
      ),
      getIdsForTopCategories(
        metadata,
        environmentResolver,
        plan.proximity?.categories || [],
      ),
      getIdsForBrands(
        metadata,
        environmentResolver,
        plan.proximity?.brands || [],
      ),
      getIdsForTags(metadata, environmentResolver, plan.proximity?.tags || []),
    ])

    const maxBufferDistance = plan.filters[0].proximity.max_distance
    const minBufferDistance = plan.filters[0].proximity.min_distance

    const countries = [
      ...new Set(plan.filters.flatMap((filter) => filter.countries)),
    ]

    const newCache: POIsFilterCache = {
      proximity: {
        subCategories: new Set(subCategories),
        topCategories: new Set(topCategories),
        brands: new Set(brands),
        locations: new Set(plan.proximity?.locations || []),
        tags: new Set(tags),
        countries: new Set(countries),
      },
      bufferDistance: [minBufferDistance, maxBufferDistance],
    }

    this.cache = newCache
    this.generation += 1
  }

  /**
   * Gets the current cached filter values
   * This is the fast path used by filterPois function
   */
  getFiltersProximityElements(): POIsFilterCacheProximityElements {
    return this.cache.proximity
  }

  getBufferDistance(): [number, number] {
    return this.cache.bufferDistance
  }

  /**
   * Clears the cache (useful for testing or reset scenarios)
   */
  clearCache(): void {
    this.cache = DEFAULT_CACHE
    this.generation += 1
  }

  isCacheEmpty(): boolean {
    const proximityElements = this.cache.proximity
    return (
      proximityElements.subCategories.size === 0 &&
      proximityElements.topCategories.size === 0 &&
      proximityElements.brands.size === 0 &&
      proximityElements.locations.size === 0 &&
      proximityElements.tags.size === 0
    )
  }
}

// Singleton instance
const poisFilterCache = new POIsFilterCacheManager()

/**
 * Updates the POIs filter cache when plan changes
 * This should be called from Vuex mutations
 */
export async function updatePOIsFilterCache(
  plan: PlanProximity,
): Promise<void> {
  // eslint-disable-next-line no-console
  console.debug('🟪 Updating POIs filter cache tileset')
  await poisFilterCache.updateCache(plan)
  // eslint-disable-next-line no-console
  console.debug('🟪 POIs filter cache tileset updated')
  if (poisFilterCache.isCacheEmpty()) {
    layerService.hideLayer(POI_LAYER, true)
  } else {
    const isLayervisible = layerService.isVisible(POI_LAYER)
    if (!isLayervisible) {
      return
    }
    console.debug('🟪 Updating POI layer after cache for tileset is updated')
    // Trigger POI layer update after cache is ready
    layerService.updateLayer(POI_LAYER, {
      updateTriggers: {
        getFilterValue: Date.now(),
      },
    })
  }
}

/**
 * Gets cached POI filter values for high-performance filtering
 * This bypasses Vuex store access during filtering
 */
export function getCachedPOIsFilters(): POIsFilterCacheProximityElements {
  return poisFilterCache.getFiltersProximityElements()
}

export function getCachedPOIsBufferDistance(): [number, number] {
  return poisFilterCache.getBufferDistance()
}

/** Changes whenever the cached filters change; see POIsFilterCacheManager. */
export function getPOIsFilterCacheGeneration(): number {
  return poisFilterCache.getGeneration()
}

export function isPOIsCacheEmpty(): boolean {
  return poisFilterCache.isCacheEmpty()
}
